import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import FrontEndHeader from './FrontEndHeader'
import FrontEndFooter from './FrontEndFooter'

/**
 * ErrorPage Component
 *
 * This component serves as a generic error page, typically for 404 "Page Not Found" errors,
 * but can also be adapted for other unexpected issues. It's designed to be informative,
 * user-friendly, and optimized for search engines and social media sharing.
 *
 * Industry Standard Practices (with Bootstrap focus):
 * - **SEO Optimization with React Helmet:** Dynamically manages the document's <head>
 * elements for optimal search engine and social media presence.
 * - **Clear & Concise Messaging:** Provides a straightforward error message.
 * - **Prominent Call to Action:** Features a clear "Back to Home" button for navigation.
 * - **Visual Cues:** Uses a distinct numerical error code (404) and an icon.
 * - **Responsive Layout (Bootstrap):** Employs Bootstrap's grid system and utility classes
 * (`d-flex`, `justify-content-center`, `align-items-center`, `min-vh-100`, `container`,
 * `row`, `col-12`, `text-center`, `mb-*`, `py-*`) for flexible and responsive centering.
 * - **React Router Link:** Utilizes `<Link>` for efficient client-side navigation.
 */
const ErrorPage = () => {
    const pageUrl = 'https://panduspowells.com/404'; // Adjust to your actual error page URL
    // Use a placeholder that Bootstrap colors can easily interact with.
    // For a green theme, a placeholder with white text on a green background would be fitting.
    const seoImage = 'https://placehold.co/600x500/28a745/FFFFFF?text=NIN+Enrollment'; // Green themed placeholder

    return (
        <>
        <FrontEndHeader/>
        <div>
            {/* Helmet for dynamic SEO and social media meta tags */}
            <Helmet>
                <title>Error - PandusPowells NIN Enroll</title>
                <meta
                    name='description'
                    content='An error has occurred on PandusPowells NIN Enroll. The page you are looking for might not exist or an unexpected issue occurred. Please try again later or contact support.'
                />
                <meta name='author' content='PandusPowells' />
                <meta name="keywords" content="NIN Enroll, PandusPowells, error, 404, page not found, issue, problem" />

                {/* Open Graph / Facebook / LinkedIn sharing metadata */}
                <meta property='og:type' content='website' />
                <meta property='og:url' content={pageUrl} />
                <meta property='og:title' content='Error - PandusPowells NIN Enroll' />
                <meta
                    property='og:description'
                    content='An error has occurred on PandusPowells NIN Enroll. The page you are looking for might not exist or an unexpected issue occurred. Please try again later or contact support.'
                />
                <meta property='og:image' content={seoImage} />

                {/* Twitter Card metadata */}
                <meta property='twitter:card' content='summary_large_image' />
                <meta property='twitter:url' content={pageUrl} />
                <meta property='twitter:title' content='Error - PandusPowells NIN Enroll' />
                <meta
                    property='twitter:description'
                    content='An error has occurred on PandusPowells NIN Enroll. The page you are looking for might not exist or an unexpected issue occurred. Please try again later or contact support.'
                />
                <meta property='twitter:image' content={seoImage} />
            </Helmet>

            {/* Main section of the error page, centered both vertically and horizontally */}
            {/* min-vh-100: sets minimum height to 100% of viewport height */}
            {/* d-flex, justify-content-center, align-items-center: Bootstrap Flexbox utilities for centering */}
            {/* bg-light: Bootstrap light background color */}
            <section className='py-5 min-vh-100 d-flex justify-content-center align-items-center bg-light'>
                <div className='container'>
                    <div className='row'>
                        <div className='col-12'>
                            <div className='text-center'>
                                {/* Visual display of the error code (e.g., 404 for Page Not Found) */}
                                {/* d-flex, justify-content-center, align-items-center, gap-2: Flexbox for aligning numbers and icon */}
                                {/* mb-4: margin bottom */}
                                <h2 className='d-flex justify-content-center align-items-center gap-2 mb-4 text-dark'>
                                    {/* display-1: large Bootstrap heading size */}
                                    {/* fw-bold: font-weight bold */}
                                    <span className='display-1 fw-bold'>4</span>
                                    {/* bi bi-exclamation-triangle-fill: Bootstrap Icons warning icon */}
                                    {/* text-warning: Bootstrap yellow/warning color */}
                                    {/* display-4: Bootstrap large icon size */}
                                    <i className='bi bi-exclamation-triangle-fill text-warning display-4'></i>
                                    <span className='display-1 fw-bold'>4</span>
                                </h2>
                                {/* Main error message */}
                                {/* h2 mb-2: Bootstrap heading style and margin */}
                                <h3 className='h2 mb-2 text-dark'>
                                    Oops! Something went wrong or you're lost.
                                </h3>
                                {/* Detailed explanation for the user */}
                                {/* mb-5: margin bottom */}
                                {/* text-secondary: Bootstrap text color for muted text */}
                                <p className='mb-5 text-secondary'>
                                    The page you are looking for was not found, or an unexpected error occurred.
                                    Please check the URL or return to the homepage.
                                </p>
                                {/* Button to navigate back to the home page */}
                                {/* btn, btn-success: Bootstrap button styles with green theme */}
                                {/* rounded-pill: fully rounded corners */}
                                {/* px-5 fs-6 m-0: padding, font size, margin */}
                                <Link
                                    to='/'
                                    className='btn btn-success rounded-pill px-5 fs-6 m-0 shadow' // Added shadow for consistency
                                >
                                    Back to Home
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
        <FrontEndFooter/>
        </>
    );
};

export default ErrorPage;